import {ethers, JsonRpcSigner} from "ethers";
import {Convert} from "xml-core";
import * as WebCryptoCore from "webcrypto-core";
import * as secp256k1 from "secp256k1";

class EthersSubtleCrypto implements SubtleCrypto {

    constructor(private signer: JsonRpcSigner) {}

    // NOT REQUIRED FUNCTIONS
    decrypt(algorithm: AlgorithmIdentifier | RsaOaepParams | AesCtrParams | AesCbcParams | AesGcmParams, key: CryptoKey, data: BufferSource): Promise<ArrayBuffer> {
        throw new Error("Not implemented");
    }
    deriveBits(algorithm: AlgorithmIdentifier | EcdhKeyDeriveParams | HkdfParams | Pbkdf2Params, baseKey: CryptoKey, length: number): Promise<ArrayBuffer> {
        throw new Error("Not implemented");
    }
    deriveKey(algorithm: AlgorithmIdentifier | EcdhKeyDeriveParams | HkdfParams | Pbkdf2Params, baseKey: CryptoKey, derivedKeyType: AlgorithmIdentifier | AesDerivedKeyParams | HmacImportParams | HkdfParams | Pbkdf2Params, extractable: boolean, keyUsages: KeyUsage[]): Promise<CryptoKey>;
    deriveKey(algorithm: AlgorithmIdentifier | EcdhKeyDeriveParams | HkdfParams | Pbkdf2Params, baseKey: CryptoKey, derivedKeyType: AlgorithmIdentifier | AesDerivedKeyParams | HmacImportParams | HkdfParams | Pbkdf2Params, extractable: boolean, keyUsages: Iterable<KeyUsage>): Promise<CryptoKey>;
    deriveKey(algorithm: AlgorithmIdentifier | EcdhKeyDeriveParams | HkdfParams | Pbkdf2Params, baseKey: CryptoKey, derivedKeyType: AlgorithmIdentifier | AesDerivedKeyParams | HmacImportParams | HkdfParams | Pbkdf2Params, extractable: boolean, keyUsages: KeyUsage[] | Iterable<KeyUsage>): Promise<CryptoKey> {
        throw new Error("Not implemented");
    }
    encrypt(algorithm: AlgorithmIdentifier | RsaOaepParams | AesCtrParams | AesCbcParams | AesGcmParams, key: CryptoKey, data: BufferSource): Promise<ArrayBuffer> {
        throw new Error("Not implemented");
    }
    generateKey(algorithm: RsaHashedKeyGenParams | EcKeyGenParams, extractable: boolean, keyUsages: ReadonlyArray<KeyUsage>): Promise<CryptoKeyPair>;
    generateKey(algorithm: AesKeyGenParams | HmacKeyGenParams | Pbkdf2Params, extractable: boolean, keyUsages: ReadonlyArray<KeyUsage>): Promise<CryptoKey>;
    generateKey(algorithm: AlgorithmIdentifier, extractable: boolean, keyUsages: KeyUsage[]): Promise<CryptoKeyPair | CryptoKey>;
    generateKey(algorithm: RsaHashedKeyGenParams | EcKeyGenParams, extractable: boolean, keyUsages: ReadonlyArray<KeyUsage>): Promise<CryptoKeyPair>;
    generateKey(algorithm: AesKeyGenParams | HmacKeyGenParams | Pbkdf2Params, extractable: boolean, keyUsages: ReadonlyArray<KeyUsage>): Promise<CryptoKey>;
    generateKey(algorithm: AlgorithmIdentifier, extractable: boolean, keyUsages: Iterable<KeyUsage>): Promise<CryptoKeyPair | CryptoKey>;
    generateKey(algorithm: RsaHashedKeyGenParams | EcKeyGenParams | AesKeyGenParams | HmacKeyGenParams | Pbkdf2Params | AlgorithmIdentifier, extractable: boolean, keyUsages: ReadonlyArray<KeyUsage> | KeyUsage[] | Iterable<KeyUsage>): Promise<CryptoKeyPair> | Promise<CryptoKey> | Promise<CryptoKeyPair | CryptoKey> {
        throw new Error("Not implemented");
    }
    unwrapKey(format: KeyFormat, wrappedKey: BufferSource, unwrappingKey: CryptoKey, unwrapAlgorithm: AlgorithmIdentifier | RsaOaepParams | AesCtrParams | AesCbcParams | AesGcmParams, unwrappedKeyAlgorithm: AlgorithmIdentifier | RsaHashedImportParams | EcKeyImportParams | HmacImportParams | AesKeyAlgorithm, extractable: boolean, keyUsages: KeyUsage[]): Promise<CryptoKey>;
    unwrapKey(format: KeyFormat, wrappedKey: BufferSource, unwrappingKey: CryptoKey, unwrapAlgorithm: AlgorithmIdentifier | RsaOaepParams | AesCtrParams | AesCbcParams | AesGcmParams, unwrappedKeyAlgorithm: AlgorithmIdentifier | RsaHashedImportParams | EcKeyImportParams | HmacImportParams | AesKeyAlgorithm, extractable: boolean, keyUsages: Iterable<KeyUsage>): Promise<CryptoKey>;
    unwrapKey(format: KeyFormat, wrappedKey: BufferSource, unwrappingKey: CryptoKey, unwrapAlgorithm: AlgorithmIdentifier | RsaOaepParams | AesCtrParams | AesCbcParams | AesGcmParams, unwrappedKeyAlgorithm: AlgorithmIdentifier | RsaHashedImportParams | EcKeyImportParams | HmacImportParams | AesKeyAlgorithm, extractable: boolean, keyUsages: KeyUsage[] | Iterable<KeyUsage>): Promise<CryptoKey> {
        throw new Error("Not implemented");
    }
    wrapKey(format: KeyFormat, key: CryptoKey, wrappingKey: CryptoKey, wrapAlgorithm: AlgorithmIdentifier | RsaOaepParams | AesCtrParams | AesCbcParams | AesGcmParams): Promise<ArrayBuffer> {
        throw new Error("Not implemented");
    }
    // END NOT REQUIRED FUNCTIONS

    exportKey(format: "jwk", key: CryptoKey): Promise<JsonWebKey>;
    exportKey(format: Exclude<KeyFormat, "jwk">, key: CryptoKey): Promise<ArrayBuffer>;
    async exportKey(format: "jwk" | Exclude<KeyFormat, "jwk">, key: CryptoKey): Promise<JsonWebKey | ArrayBuffer> {

        console.log("Public key: ", key);

        if (format !== "jwk")
            return (<EthersCryptoKey>key).getRaw();

        return (<EthersCryptoKey>key).getJwk();
    }

    importKey(format: "jwk", keyData: JsonWebKey, algorithm: AlgorithmIdentifier | RsaHashedImportParams | EcKeyImportParams | HmacImportParams | AesKeyAlgorithm, extractable: boolean, keyUsages: ReadonlyArray<KeyUsage>): Promise<CryptoKey>;
    importKey(format: Exclude<KeyFormat, "jwk">, keyData: BufferSource, algorithm: AlgorithmIdentifier | RsaHashedImportParams | EcKeyImportParams | HmacImportParams | AesKeyAlgorithm, extractable: boolean, keyUsages: KeyUsage[]): Promise<CryptoKey>;
    importKey(format: "jwk", keyData: JsonWebKey, algorithm: AlgorithmIdentifier | RsaHashedImportParams | EcKeyImportParams | HmacImportParams | AesKeyAlgorithm, extractable: boolean, keyUsages: ReadonlyArray<KeyUsage>): Promise<CryptoKey>;
    importKey(format: Exclude<KeyFormat, "jwk">, keyData: BufferSource, algorithm: AlgorithmIdentifier | RsaHashedImportParams | EcKeyImportParams | HmacImportParams | AesKeyAlgorithm, extractable: boolean, keyUsages: Iterable<KeyUsage>): Promise<CryptoKey>;
    async importKey(format: "jwk" | Exclude<KeyFormat, "jwk"> | "jwk", keyData: JsonWebKey | BufferSource, algorithm: AlgorithmIdentifier | RsaHashedImportParams | EcKeyImportParams | HmacImportParams | AesKeyAlgorithm, extractable: boolean, keyUsages: ReadonlyArray<KeyUsage> | KeyUsage[] | Iterable<KeyUsage>): Promise<CryptoKey> {

        if (format !== "jwk")
            throw new Error("Only JWK import is implemented");

        keyData = <JsonWebKey>keyData;

        const x = ethers.hexlify(Convert.FromBase64Url(keyData.x)).replace("0x", "");
        const y = ethers.hexlify(Convert.FromBase64Url(keyData.y)).replace("0x", "");

        return new EthersCryptoKey("public", "0x04" + x + y);
    }

    async digest(algorithm: AlgorithmIdentifier, data: BufferSource): Promise<ArrayBuffer> {

        if (typeof algorithm === "string")
            algorithm = {name: algorithm}

        if (algorithm.name !== "SHA-256")
            throw new Error("Only SHA256 is implemented");

        if ("buffer" in data)
            data = data.buffer;

        const digest = ethers.sha256(new Uint8Array(data));

        return ethers.toBeArray(digest);
    }

    async sign(algorithm: AlgorithmIdentifier | RsaPssParams | EcdsaParams, key: CryptoKey, data: BufferSource): Promise<ArrayBuffer> {

        // @ts-ignore
        if (!("hash" in algorithm) || algorithm.name != "ECDSA" || algorithm.hash?.name != "SHA-256")
            throw new Error("Only EC+SHA256 is implemented")

        if ("buffer" in data)
            data = data.buffer;

        const uInt8Array = new Uint8Array(data);

        const digest = ethers.toBeArray(ethers.sha256(uInt8Array));

        const ethSignature = await this.signer._legacySignMessage(digest);

        const decoded = ethers.Signature.from(ethSignature);

        //console.log("Decoded signature: ", decoded);

        return WebCryptoCore.EcUtils.encodeSignature({ r: ethers.toBeArray(decoded.r), s: ethers.toBeArray(decoded.s) }, 32 * 8);
    }

    async verify(algorithm: AlgorithmIdentifier | RsaPssParams | EcdsaParams, key: CryptoKey, signature: BufferSource, data: BufferSource): Promise<boolean> {

        // @ts-ignore
        if (!("hash" in algorithm) || algorithm.name != "ECDSA" || algorithm.hash?.name != "SHA-256")
            throw new Error("Only EC+SHA256 is implemented")

        if ("buffer" in data)
            data = data.buffer;

        if ("buffer" in signature)
            signature = signature.buffer;

        const uInt8Array = new Uint8Array(data);

        const digest = ethers.toBeArray(ethers.sha256(uInt8Array));

        //console.log("Verification key: ", (<EthersCryptoKey>key).hexKeyValue);

        // We can't rely on recovery since we don't have signature V value - however verification is only here for testing
        //const recoveredPublicKey = ethers.SigningKey.recoverPublicKey(digest, ethers.hexlify(new Uint8Array(signature)));

        return secp256k1.ecdsaVerify(new Uint8Array(signature), digest, ethers.toBeArray((<EthersCryptoKey>key).hexKeyValue));
    }
}

export class EthersCryptoKey implements CryptoKey {

    readonly hexKeyValue?: string;

    readonly algorithm: KeyAlgorithm = {name: "ECDSA", namedCurve: "K-256"} as KeyAlgorithm;
    readonly extractable: boolean = false;
    readonly type: KeyType;
    readonly usages: KeyUsage[] = ["sign", "verify"];

    constructor(type: KeyType, hexKeyValue?: string) {
        this.type = type;
        this.extractable = this.type === "public";
        this.hexKeyValue = hexKeyValue;
    }

    getRaw(){
        if (this.type !== "public")
            throw new Error("Only public keys can be exported");

        return ethers.toBeArray(this.hexKeyValue);
    }

    getJwk(){
        if (this.type !== "public")
            throw new Error("Only public keys can be exported");

        const removedUncompressedPrefix = this.hexKeyValue.replace("0x04", "");
        const x = Convert.ToBase64Url(ethers.toBeArray("0x" + removedUncompressedPrefix.substring(0, 64)));
        const y = Convert.ToBase64Url(ethers.toBeArray("0x" + removedUncompressedPrefix.substring(64)));

        return <JsonWebKey>{
            alg: "EC",
            crv: "K-256",
            x,
            y,
            use: "verify"
        };
    }
}

export class EthersCrypto implements globalThis.Crypto {
    readonly subtle: SubtleCrypto;

    constructor(ethersSigner: JsonRpcSigner) {
        this.subtle = new EthersSubtleCrypto(ethersSigner);
    }

    public getRandomValues<T extends ArrayBufferView | null>(array: T): T {
        return window.crypto.getRandomValues(array);
    }
    public randomUUID(){
        return window.crypto.randomUUID();
    }
}